import { Controller } from '@hotwired/stimulus'
import Sortable from 'sortablejs'
import * as Turbo from '@hotwired/turbo'

export default class extends Controller {
  static targets = ['header']

  connect() {
    this.initializeSortable()
  }

  disconnect() {
    if (this.sortableInstance) {
      this.sortableInstance.destroy()
      this.sortableInstance = null
    }
  }

  initializeSortable() {
    const headerRow = this.hasHeaderTarget ? this.headerTarget : this.element

    if (!headerRow) {
      console.warn('Column reorder: Could not find header row')
      return
    }

    this.sortableInstance = new Sortable(headerRow, {
      animation: 150,
      // Only allow dragging columns with the drag handle, exclude title and checkbox
      handle: '[data-column-drag-handle]',
      ghostClass: 'column-drag-ghost',
      chosenClass: 'column-drag-chosen',
      dragClass: 'column-dragging',
      // Don't allow dragging checkbox or title columns (they don't have handles anyway)
      filter: '[data-task-table-target="checkboxColumn"], [data-task-table-target="titleColumn"]',

      onMove: (event) => {
        // Prevent dropping before the title column (or checkbox if present)
        // related is the element we're moving relative to
        const related = event.related
        const relatedTarget = related?.dataset?.taskTableTarget

        // If trying to move before checkbox or title, cancel the move
        if (relatedTarget === 'checkboxColumn' || relatedTarget === 'titleColumn') {
          // Only block if we're trying to insert BEFORE (not after)
          if (!event.willInsertAfter) {
            return false
          }
        }
        return true
      },

      onEnd: (event) => {
        if (event.oldIndex === event.newIndex) return
        this.updateColumnOrder()
      }
    })
  }

  updateColumnOrder() {
    const headerRow = this.hasHeaderTarget ? this.headerTarget : this.element
    const columns = []

    // Collect all column elements and extract their column IDs
    headerRow.querySelectorAll('[data-task-table-target]').forEach(col => {
      const columnId = this.getColumnId(col)
      if (columnId && columnId !== 'checkbox') {
        columns.push(columnId)
      }
    })

    this.navigateWithNewOrder(columns)
  }

  getColumnId(element) {
    // First check for explicit column-id data attribute (used for task type columns)
    if (element.dataset.columnId) {
      return element.dataset.columnId
    }

    // Extract from target name (e.g., 'titleColumn' -> 'title', 'dueDateColumn' -> 'due_date')
    const target = element.dataset.taskTableTarget
    if (!target) return null

    // Remove 'Column' suffix and convert camelCase to snake_case
    const name = target.replace('Column', '')
    return this.camelToSnake(name)
  }

  camelToSnake(str) {
    return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
  }

  navigateWithNewOrder(columns) {
    const url = new URL(window.location.href)

    // Remove existing columns params
    url.searchParams.delete('columns[]')

    // Add columns in the new order
    columns.forEach(col => {
      url.searchParams.append('columns[]', col)
    })

    // Use Turbo to navigate with the new column order
    Turbo.visit(url.toString(), { action: 'advance' })
  }
}
